import { Barbell, Timer, ArrowClockwise, Target, Lightning, Stack, Flag, Sun, PersonSimpleTaiChi, Shield, Waveform } from "@phosphor-icons/react";
import { describeIntervals } from "@/lib/intervals";
import { formatDuration } from "@/lib/api";

export const BLOCK_TYPES = {
  warmup:       { label: "Warm Up", icon: Sun, tint: "bg-amber-500 text-white", desc: "Prep the body — dynamic movements to get warm and loose." },
  mobility:     { label: "Mobility", icon: PersonSimpleTaiChi, tint: "bg-sky-600 text-white", desc: "Stretch, mobilize, cool down. Time-based holds and flows." },
  strength:     { label: "Strength", icon: Barbell, tint: "bg-moss text-white", desc: "Sets × reps × weight. Log each set." },
  metcon:       { label: "MetCon (RFT)", icon: Timer, tint: "bg-terracotta text-white", desc: "Rounds for time — complete N rounds as fast as possible." },
  amrap:        { label: "AMRAP", icon: ArrowClockwise, tint: "bg-warning text-white", desc: "As Many Rounds (or Reps) As Possible in a time cap." },
  emom:         { label: "EMOM", icon: Target, tint: "bg-chart-3 text-white", desc: "Every Minute On the Minute for N minutes." },
  tabata:       { label: "Tabata", icon: Lightning, tint: "bg-error text-white", desc: "8 rounds of 20s work / 10s rest (default)." },
  ladder:       { label: "Ladder", icon: Stack, tint: "bg-chart-2 text-white", desc: "Descending/ascending reps (e.g., 21-15-9)." },
  circuit:      { label: "Circuit / Superset", icon: Flag, tint: "bg-ink-2 text-white", desc: "Compound or superset — multiple movements in a series. Logged as sets/reps/weights, no timer." },
  intervals:    { label: "Intervals", icon: Waveform, tint: "bg-teal-700 text-white", desc: "Repeat efforts with rest — time for distance/calories, or max calories/distance in a set time." },
  self_defense: { label: "Self Defense", icon: Shield, tint: "bg-slate-800 text-white", desc: "Drills, techniques and sparring rounds. Same movement metrics as any other block — sets, reps, weight, time, distance, height, cal, RPE." },
};

export function describeBlock(blk) {
  const sc = blk.scheme_config || {};
  const repsStr = (sc.rep_scheme && sc.rep_scheme.length) ? sc.rep_scheme.join("-") : null;
  switch (blk.type) {
    case "metcon": {
      const parts = [];
      if (repsStr) parts.push(repsStr);
      else if (sc.rounds) parts.push(`${sc.rounds} Rounds for Time`);
      if (sc.time_cap_seconds) parts.push(`cap ${Math.round(sc.time_cap_seconds / 60)}:${String(sc.time_cap_seconds % 60).padStart(2, "0")}`);
      return parts.join(" · ") || "Rounds for Time";
    }
    case "amrap": {
      const parts = [];
      if (sc.time_cap_seconds) parts.push(`AMRAP ${Math.round(sc.time_cap_seconds / 60)}:${String(sc.time_cap_seconds % 60).padStart(2, "0")}`);
      else parts.push("AMRAP");
      if (repsStr) parts.push(repsStr);
      return parts.join(" · ");
    }
    case "emom":
      return `EMOM ${sc.rounds || "?"} · every ${formatDuration(sc.interval_seconds || 60)}${sc.rest_seconds ? ` · rest ${formatDuration(sc.rest_seconds)}` : ""}`;
    case "intervals":
      return describeIntervals(sc);
    case "tabata":
      return `Tabata · ${sc.rounds || 8}× ${sc.work_seconds || 20}s on / ${sc.rest_seconds || 10}s off`;
    case "ladder":
      return `Ladder · ${repsStr || "reps"}`;
    case "circuit":
      return `Superset · ${(blk.movements || []).length} movements`;
    default:
      return null;
  }
}

export function fmtCap(sec) {
  if (!sec) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}:${String(s).padStart(2, "0")}` : `${m} min`;
}

export function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2];
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2];
    }
  } catch {
    return null;
  }
  return null;
}
